import {
  Box,
  IconButton,
  Button,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import "react-toastify/dist/ReactToastify.css";
import Nodata from "../../../assets/errors/nodata.png";

import Register from "./registration/Register";

const Students = () => {
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [nextOfKeen, setNextOfKeen] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openPersonDialog, setOpenPersonDialog] = useState(false);
  const [openModuleDialog, setOpenModuleDialog] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData, userRole } = useAuth();

  const generateToken = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const [userToken, setUserToken] = useState(() => generateToken())

  const [formData, setFormData] = useState({
    registration_type: "",
    next_of_keen_id: "",
    parent_id: "",
    parent_relationship: "",
    firstName: "",
    lastName: "",
    category: "",
    cell: "",
    email: "",
    password: "",
    studentToken: userToken,
    age: "",
    school: "",
    grade: "",
    physicalAddress: "",
    status: "",
    date: "",
    image: ""
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const [
          studentsResponse,
          parentsResponse,
          nextOfKeenResponse,
          modulesResponse,
        ] = await Promise.all([
          getData("proccess.php", "students_V2"),
          getData("proccess.php", "parent_V2"),
          getData("proccess.php", "next_of_keen_V2"),
          getData("proccess.php", "modules_V2"),
        ]);

        if (studentsResponse.success) {
          setStudents(studentsResponse.data);
        } else {
          toast.error(
            studentsResponse.message || "Failed to load student data"
          );
        }

        if (parentsResponse.success) {
          setParents(parentsResponse.data);
        } else {
          toast.error(parentsResponse.message || "Failed to load parent data");
        }

        if (nextOfKeenResponse.success) {
          setNextOfKeen(nextOfKeenResponse.data);
        } else {
          toast.error(
            nextOfKeenResponse.message || "Failed to load next of keen data"
          );
        }

        if (modulesResponse.success) {
          setModules(modulesResponse.data);
        } else {
          toast.error(modulesResponse.message || "Failed to load module data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch data");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  // Export to Excel function
  const exportToExcel = () => {
    if (students.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      // Create CSV content
      const headers = [
        'ID',
        'Registration Type',
        'Next of Keen',
        'Parent',
        'First Name',
        'Last Name',
        'Cell',
        'Email',
        'Age',
        'School',
        'Grade',
        'Physical Address',
        'Module',
        'Status',
        'Transaction Token',
        'Transaction Status',
        'Transaction Result',
        'Due to Us',
        'Due to You',
        'Date Created'
      ];

      const csvContent = students.map(student => {
        // Find related data
        const nextOfKeenPerson = nextOfKeen.find(n => n.id === student.next_of_keen_id);
        const parentPerson = parents.find(p => p.id === student.parent_id);
        const module = modules.find(m => m.id === student.course);

        const nextOfKeenText = nextOfKeenPerson ? 
          `${nextOfKeenPerson.FirstName} ${nextOfKeenPerson.LastName}` : 'Unknown';
        const parentText = parentPerson ? 
          `${parentPerson.FirstName} ${parentPerson.LastName}` : 'Unknown';
        const moduleText = module ? module.name : 'Unknown';

        return [
          student.id,
          `"${student.registration_type || ''}"`,
          `"${nextOfKeenText}"`,
          `"${parentText}"`,
          `"${student.firstName || ''}"`,
          `"${student.lastName || ''}"`,
          `"${student.cell || ''}"`,
          `"${student.email || ''}"`,
          student.age || '',
          `"${student.school || ''}"`,
          `"${student.grade || ''}"`,
          `"${student.physicalAddress || ''}"`,
          `"${moduleText}"`,
          `"${student.status || ''}"`,
          `"${student.trans_token || ''}"`,
          `"${student.trans_status || ''}"`,
          `"${student.trans_result_explanation || ''}"`,
          student.dueToUs || '0',
          student.dueToYou || '0',
          `"${student.date || ''}"`
        ].join(',');
      });

      // Combine headers and data
      const csv = [headers.join(','), ...csvContent].join('\n');

      // Create and download file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `students_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Students data exported successfully");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data");
    }
  };

  const handleAddClick = () => {
    setOpenAddDialog(true);
  };

  const handleEditClick = (student) => {
    setSelectedStudent(student);
    setFormData({
      registration_type: student.registration_type || "",
      next_of_keen_id: student.next_of_keen_id || "",
      parent_id: student.parent_id || "",
      firstName: student.firstName || "",
      lastName: student.lastName || "",
      cell: student.cell || "",
      email: student.email || "",
      password: "", // Password not pre-filled for security
      age: student.age || "",
      school: student.school || "",
      grade: student.grade || "",
      physicalAddress: student.physicalAddress || "",
      course: student.course || "",
    });
    setOpenEditDialog(true);
  };

  const handlePersonClick = (personId, table) => {
    const person =
      table === "parent_V2"
        ? parents.find((p) => p.id === personId)
        : nextOfKeen.find((n) => n.id === personId);
    setSelectedPerson(person || null);
    setOpenPersonDialog(true);
  };

  const handleModuleClick = (moduleId) => {
    const module = modules.find((m) => m.id === moduleId);
    setSelectedModule(module || null);
    setOpenModuleDialog(true);
  };

  const handleDeleteClick = (student) => {
    setStudentToDelete(student);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData(
        "proccess.php",
        "students_V2",
        studentToDelete.id
      );
      if (response.success) {
        toast.success(
          `Student "${studentToDelete.firstName} ${studentToDelete.lastName}" deleted successfully`
        );
        setStudents(
          students.filter((student) => student.id !== studentToDelete.id)
        );
      } else {
        toast.error(response.message || "Failed to delete student");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete student");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setStudentToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedStudent) {
        response = await updateData(
          "proccess.php",
          "students_V2",
          formData,
          selectedStudent.id
        );
        if (response.success) {
          toast.success(`Student updated successfully`);
          setStudents((prev) =>
            prev.map((s) =>
              s.id === selectedStudent.id ? { ...s, ...formData } : s
            )
          );
        }
      } else {
        response = await addData("proccess.php", "students_V2", formData);
        if (response.success) {
          toast.success(`Student added successfully`);
          setStudents((prev) => [
            ...prev,
            { ...formData, id: response.data.id },
          ]);
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedStudent(null);
  };

  const columns = [
    { field: "id", headerName: "Student Number", flex: 0.5, minWidth: 100 },
    {
      field: "registration_type",
      headerName: "Registration Type",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "next_of_keen_id",
      headerName: "Next of Keen",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const person = nextOfKeen.find(
          (n) => n.id === params.row?.next_of_keen_id
        );
        return (
          <Box width="100%">
            <Typography
              sx={{
                cursor: person ? "pointer" : "default",
                color: person ? colors.blue[400] : "inherit",
              }}
              onClick={() =>
                person &&
                handlePersonClick(params.row.next_of_keen_id, "next_of_keen_V2")
              }
            >
              {person ? `${person.fullName}` : "Unknown"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "parent_id",
      headerName: "Parent",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const person = parents.find((p) => p.id === params.row?.parent_id);
        return (
          <Box width="100%">
            <Typography
              sx={{
                cursor: person ? "pointer" : "default",
                color: person ? colors.blue[400] : "inherit",
              }}
              onClick={() =>
                person && handlePersonClick(params.row.parent_id, "parent_V2")
              }
            >
              {person ? `${person.fullName}` : "Unknown"}
            </Typography>
          </Box>
        );
      },
    },
    { field: "parent_relationship", headerName: "Parent Relationship", flex: 1, minWidth: 100 },
    { field: "firstName", headerName: "First Name", flex: 1, minWidth: 100 },
    { field: "lastName", headerName: "Last Name", flex: 1, minWidth: 100 },
    { field: "category", headerName: "Category", flex: 1, minWidth: 100 },
    { field: "cell", headerName: "Cell", flex: 1, minWidth: 100 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 120 },
    { field: "studentToken", headerName: "studentToken", flex: 1, minWidth: 100 },
    { field: "age", headerName: "Age", flex: 0.5, minWidth: 50 },
    { field: "school", headerName: "School", flex: 1, minWidth: 120 },
    { field: "grade", headerName: "Grade", flex: 0.5, minWidth: 50 },
    {
      field: "physicalAddress",
      headerName: "Physical Address",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 80,
      renderCell: (params) => {
        return (
          <Box>
            <Typography color={params.row.status == "1" ? "success" : "error"}>
              {params.row.status == "1" ? "Active" : "Inactive"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "date",
      headerName: "Registration Date",
      flex: 1,
      minWidth: 120,
    },
    // {
    //   field: "image",
    //   headerName: "Image",
    //   flex: 1,
    //   minWidth: 80,
    //   renderCell: (params) => {
    //     return (
    //       <Box>
    //         {params.row.image ? (
    //           <img
    //             src={params.row.image}
    //             alt="Student"
    //             style={{ width: 50, height: 50, objectFit: "cover" }}
    //           />
    //         ) : (
    //           <Typography>No Image</Typography>
    //         )}
    //       </Box>
    //     );
    //   },
    // },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.red[400] }}
            disabled={userRole !== "admin" && userRole !== "hr"}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No students available</Typography>
      </Box>
    );
  };

  return (
    <Box m="10px">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Person Details Dialog (for Parent and Next of Keen) */}
      <Dialog
        open={openPersonDialog}
        onClose={() => setOpenPersonDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Person Details</DialogTitle>
        <DialogContent>
          {selectedPerson ? (
            <Box>
              <Typography>
                <strong>Name:</strong> {selectedPerson.fullName}
              </Typography>
              <Typography>
                <strong>Email:</strong> {selectedPerson.email}
              </Typography>
              <Typography>
                <strong>Cell:</strong> {selectedPerson.cell || "N/A"}
              </Typography>
              <Typography>
                <strong>Status:</strong> {selectedPerson.status === "1" ? "Active" : "Inactive"}
              </Typography>
            </Box>
          ) : (
            <Typography>No person details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPersonDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Module Details Dialog */}
      <Dialog
        open={openModuleDialog}
        onClose={() => setOpenModuleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Module Details</DialogTitle>
        <DialogContent>
          {selectedModule ? (
            <Box>
              <Typography>
                <strong>Name:</strong> {selectedModule.name}
              </Typography>
              <Typography>
                <strong>Code:</strong> {selectedModule.code}
              </Typography>
              <Typography>
                <strong>Description:</strong>{" "}
                {selectedModule.description || "N/A"}
              </Typography>
              <Typography>
                <strong>Instructor:</strong>{" "}
                {parents.find((p) => p.Id === selectedModule.instructor_id)
                  ?.FirstName +
                  " " +
                  parents.find((p) => p.Id === selectedModule.instructor_id)
                    ?.LastName || "Unknown"}
              </Typography>
              <Typography>
                <strong>Start Date:</strong>{" "}
                {selectedModule.start_date || "N/A"}
              </Typography>
              <Typography>
                <strong>End Date:</strong> {selectedModule.end_date || "N/A"}
              </Typography>
              <Typography>
                <strong>Status:</strong> {selectedModule.status}
              </Typography>
              <Typography>
                <strong>Created At:</strong> {selectedModule.created_at}
              </Typography>
            </Box>
          ) : (
            <Typography>No module details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModuleDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => {
          setOpenAddDialog(false);
          setSelectedStudent(null);
        }}
        maxWidth="1200px"
        fullWidth
      >
        <DialogTitle>Add New Student</DialogTitle>

        <DialogContent>
          <Register />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedStudent(null);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedStudent(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedStudent ? "Edit Student" : "Add New Student"}
        </DialogTitle>
        <DialogContent>
          <Form
            onSubmit={handleSubmit}
            style={{ width: "100%", padding: "1rem" }}
          >
            <Box
              display="flex"
              gap="1rem"
              width="100%"
              mb="1rem"
              flexWrap="wrap"
            >
              <TextField
                select
                style={{ width: "48%" }}
                id="registration_type"
                name="registration_type"
                label="Registration Type"
                variant="standard"
                value={formData.registration_type}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                <option value="parent">On be half of my child</option>
                <option value="self">Self Registration</option>
                <option value="Online">Online</option>
              </TextField>
              <TextField
                select
                style={{ width: "48%" }}
                id="next_of_keen_id"
                name="next_of_keen_id"
                label="Next of Keen"
                variant="standard"
                value={formData.next_of_keen_id}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {nextOfKeen.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.fullName} {person.id}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                style={{ width: "48%" }}
                id="parent_id"
                name="parent_id"
                label="Parent"
                variant="standard"
                value={formData.parent_id}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {parents.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.fullName} {person.id}
                  </option>
                ))}
              </TextField>
              <TextField
                style={{ width: "48%" }}
                id="firstName"
                name="firstName"
                label="First Name"
                variant="standard"
                value={formData.firstName}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "48%" }}
                id="lastName"
                name="lastName"
                label="Last Name"
                variant="standard"
                value={formData.lastName}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "48%" }}
                id="cell"
                name="cell"
                label="Cell"
                variant="standard"
                value={formData.cell}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "48%" }}
                id="email"
                name="email"
                label="Email"
                type="email"
                variant="standard"
                value={formData.email}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "48%" }}
                id="password"
                name="password"
                label="Password"
                type="password"
                variant="standard"
                value={formData.password}
                onChange={handleFormChange}
                required={!selectedStudent}
              />
              <TextField
                style={{ width: "48%" }}
                id="age"
                name="age"
                label="Age"
                type="number"
                variant="standard"
                value={formData.age}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "48%" }}
                id="school"
                name="school"
                label="School"
                variant="standard"
                value={formData.school}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "48%" }}
                id="grade"
                name="grade"
                label="Grade"
                variant="standard"
                value={formData.grade}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "48%" }}
                id="physicalAddress"
                name="physicalAddress"
                label="Physical Address"
                variant="standard"
                value={formData.physicalAddress}
                onChange={handleFormChange}
                required
              />
              <TextField
                select
                style={{ width: "48%" }}
                id="course"
                name="course"
                label="Module"
                variant="standard"
                value={formData.course}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {modules.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.module_name}
                  </option>
                ))}
              </TextField>
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedStudent(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedStudent ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{studentToDelete?.firstName}{" "}
            {studentToDelete?.lastName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        mb="30px"
        ml="15px"
        mr="15px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight="bold"
            sx={{ mb: "5px" }}
          >
            Students Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage student records
          </Typography>
        </Box>
        <Box display="flex" gap="1rem">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{
              borderColor: colors.green[400],
              color: colors.green[400],
              '&:hover': {
                backgroundColor: colors.green[50],
                borderColor: colors.green[500],
              }
            }}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              "&:hover": {
                backgroundColor: colors.blue[500],
              },
            }}
          >
            Add Student
          </Button>
        </Box>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "calc(100vh - 150px)", width: "100%" }}>
        <DataGrid
          rows={students}
          columns={columns}
          getRowId={(row) => row.id || Math.random().toString(36).substr(2, 9)}
          loading={loading}
          sx={{
            border: "none",
            backgroundColor: colors.primary[500],
            ".MuiDataGrid-cell": {
              borderBottom: "none",
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Students;